import React, { useState } from 'react';
import { useAuth } from '../store/AuthContext';
import { Button } from './Button';
import { Bug, User as UserIcon, X, ChevronUp, ChevronDown } from 'lucide-react';

const MOCK_USERS = [
    { name: 'Alice', email: 'alice@example.com' },
    { name: 'Bob', email: 'bob@example.com' },
    { name: 'Charlie', email: 'charlie@example.com' },
    { name: 'Diana', email: 'diana@example.com' },
];

export const DevTools: React.FC = () => {
    const { signInWithEmail, user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);

    // Only show when emulators are configured
    const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';
    if (!useEmulators) return null;

    const handleSwitchUser = async (email: string) => {
        try {
            await signInWithEmail(email, 'password123');
        } catch (error) {
            console.error("Failed to switch user", error);
            alert("Failed to switch user. Ensure emulator is running and seeded.");
        }
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => { setIsOpen(true); }}
                className="fixed bottom-20 right-4 z-[9999] flex h-12 w-12 items-center justify-center rounded-full bg-amber-600 text-white shadow-2xl transition-transform hover:scale-110 active:scale-95 opacity-90 hover:opacity-100"
                title="Open DevTools"
            >
                <Bug size={24} />
            </button>
        );
    }

    return (
        <div className="fixed bottom-20 right-4 z-[9999] w-64 overflow-hidden rounded-xl border border-amber-900/20 bg-slate-900/95 shadow-2xl backdrop-blur-md">
            <div
                className="flex items-center justify-between bg-amber-600/20 px-4 py-2 cursor-pointer border-b border-amber-900/10"
                onClick={() => { setIsExpanded(!isExpanded); }}
            >
                <div className="flex items-center gap-2">
                    <Bug size={16} className="text-amber-500" />
                    <span className="text-xs font-bold text-amber-500 uppercase tracking-wider">Dev Tools</span>
                </div>
                <div className="flex items-center gap-1">
                    <button className="text-amber-500 hover:text-amber-400">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                        className="text-amber-500 hover:text-amber-400 ml-2"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="p-4 space-y-4">
                    <div>
                        <p className="mb-2 text-[10px] font-bold uppercase text-slate-500">Quick Switch</p>
                        <div className="grid grid-cols-2 gap-2">
                            {MOCK_USERS.map((mockUser) => {
                                const isActive = user?.email === mockUser.email;
                                return (
                                    <Button
                                        key={mockUser.email}
                                        variant={isActive ? "primary" : "outline"}
                                        className={`h-8 text-xs justify-start px-2 ${isActive ? 'bg-amber-600 hover:bg-amber-700 border-transparent' : 'border-slate-700 hover:bg-slate-800 text-slate-300'}`}
                                        onClick={() => handleSwitchUser(mockUser.email)}
                                    >
                                        <UserIcon size={12} className="mr-2 opacity-70" />
                                        {mockUser.name}
                                    </Button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="rounded bg-black/20 p-2">
                        <p className="text-[10px] text-slate-500 font-mono">
                            Current: <span className="text-slate-300">{user?.displayName ?? 'Guest'}</span>
                        </p>
                        <p className="text-[10px] text-slate-600 font-mono truncate">
                            {user?.uid ?? 'No UID'}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
